<script lang="ts">
  /**
   * The match has stopped, and then it counts back in: two moments of one
   * thing, on the shell's `ServerOverlay` (`docs/decisions/0054`). Every
   * connected participant sees it, spectators included, because two boards
   * that have both stopped owe everyone a reason.
   *
   * - **Stopped** (`seconds` is `null`): who the match is waiting for, and
   *   how long each of them has. The lines come from the room roster
   *   (`waiting`), where presence, the grace countdown and the pause budget
   *   are broadcast for both seats, so nothing here is guessed.
   * - **Everyone's back** (`seconds` is a number): the count to the moment
   *   play resumes, the same number on every screen in the room, as the
   *   card's figure (read out in its last three seconds).
   *
   * The card is the server's: Back, Escape and a press beside it never close
   * it. Back is reported (`onBackAttempt`) and the room decides; Leave room is
   * always offered, and Try to rejoin while this client's own socket is down.
   */
  import { ServerOverlay } from '@artibens/app-shell';
  import type { SheetBackAttempt } from '@artibens/app-shell';

  interface Props {
    /** `paused`, `frozen` or `resuming`: the server's word for where the match is. */
    readonly phase: string;
    /** One line per seat being waited for, already worded ("Ada is in the background · 42s of pause left"). */
    readonly waiting: readonly string[];
    /** Whole seconds to the resume, at least 1, or `null` while still stopped. */
    readonly seconds: number | null;
    /** This client's own socket is down, so there is something to retry. */
    readonly canRejoin: boolean;
    readonly onRejoin: () => void;
    readonly onLeave: () => void;
    readonly onBackAttempt: (reason: SheetBackAttempt) => void;
  }
  const { phase, waiting, seconds, canRejoin, onRejoin, onLeave, onBackAttempt }: Props = $props();

  const title = $derived(
    seconds !== null ? "Everyone's back" : phase === 'frozen' ? 'Match held' : 'Match paused',
  );
  const detail = $derived.by(() => {
    if (seconds !== null) return 'Play resumes together, from the exact moment it stopped.';
    if (phase === 'frozen') {
      return 'Nothing has been decided. The match picks up from this exact moment when everyone is back.';
    }
    return 'Both boards are stopped. Nothing lands, nothing counts down, nothing is decided.';
  });
</script>

<ServerOverlay
  open
  {title}
  eyebrow={seconds !== null ? 'Resuming in' : undefined}
  figure={seconds ?? undefined}
  {detail}
  primary={canRejoin
    ? { label: 'Try to rejoin', onclick: onRejoin, testid: 'duel-rejoin' }
    : undefined}
  secondary={{ label: 'Leave room', onclick: onLeave, testid: 'duel-pause-leave' }}
  onbackattempt={(reason) => {
    if (reason === 'back') onBackAttempt(reason);
  }}
  testid="duel-pause"
  testids={{ title: 'duel-pause-title' }}
  figureTestid="duel-resume-count"
>
  {#if seconds === null}
    <ul
      class="waiting"
      aria-label="Waiting for"
      data-testid="duel-pause-waiting"
      data-phase={phase}
    >
      {#each waiting as line, index (index)}
        <li>{line}</li>
      {/each}
      {#if waiting.length === 0}
        <li>Waiting for the match to pick up again.</li>
      {/if}
    </ul>
  {/if}
</ServerOverlay>

<style>
  .waiting {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--ui-space-1);
    text-align: center;
    font: var(--ui-text-label);
    color: var(--ui-ink);
  }
</style>
